import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import { Projects } from "data/projects";
const RecentWork = () => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const projectsToShow = windowWidth >= 1024 ? 8 : 4;
  const allServices = [
    "All",
    ...Array.from(new Set(Projects.flatMap((project) => project.services))),
  ];

  const [activeService, setActiveService] = useState("All");

  const filteredProjects =
    activeService === "All"
      ? Projects
      : Projects.filter((project) => project.services.includes(activeService));

  return (
    <section data-theme="light" className="py-4   bg-white">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-4 sm:mb-8 md:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-2">
            Recent <span className="text-accent">Projects</span>
          </h1>
          <p className="mx-auto max-w-3xl text-base text-text-secondary sm:text-lg md:text-xl mb-8">
            Discover our latest corporate films, executive portraits, and brand
            storytelling projects that demonstrate our commitment to cinematic
            excellence and technical precision.
          </p>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center">
            {allServices.map((service) => (
              <button
                key={service}
                onClick={() => setActiveService(service)}
                className={`whitespace-nowrap rounded-lg px-4 py-1 text-xs sm:text-sm font-medium transition ${
                  activeService === service
                    ? "bg-accent text-white"
                    : "border border-border bg-white text-text-primary hover:bg-accent/10 hover:text-accent"
                }`}
              >
                {service}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProjects.slice(0, projectsToShow).map((project) => (
            <div
              key={project.id}
              className="group relative bg-white rounded-xl overflow-hidden shadow-subtle hover:shadow-elevation cinematic-transition hover-lift"
              onMouseEnter={() => setHoveredItem(project.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Project image/Video */}
              <div className="relative h-40 sm:h-56 md:h-64 overflow-hidden">
                {hoveredItem === project.id ? (
                  // Only show video if it exists in the gallery
                  project.gallery?.some((item) => item.type === "video") ? (
                    <video
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                    >
                      <source
                        src={
                          project.gallery.find((item) => item.type === "video")
                            ?.src
                        }
                        type="video/mp4"
                      />
                    </video>
                  ) : project.image ? (
                    <Image
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : project.website ? (
                    <iframe
                      src={project.website}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      loading="eager"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                      referrerPolicy="no-referrer"
                    />
                  ) : null
                ) : project.image ? (
                  <Image
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                ) : project.website ? (
                  <iframe
                    src={project.website}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    loading="eager"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    referrerPolicy="no-referrer"
                  />
                ) : null}

                {/* Play Overlay */}
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 cinematic-transition">
                  <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                    <Icon name="Play" size={24} color="white" strokeWidth={2} />
                  </div>
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-accent text-white text-xs sm:text-sm font-bold rounded-full">
                    {project.services[0]}
                  </span>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-4 sm:p-6">
                <div className="mb-2">
                  <h3 className="text-lg sm:text-xl font-semibold text-primary mb-1 group-hover:text-accent cinematic-transition">
                    {project.title}
                  </h3>
                  <p className="text-accent text-xs sm:text-sm font-medium">
                    {project.client}
                  </p>
                </div>

                <p className="text-text-secondary text-sm mb-2 line-clamp-2">
                  {project.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.slice(0, 1).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                  {project.tags.length > 2 && (
                    <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md">
                      +{project.tags.length - 2} more
                    </span>
                  )}
                </div>

                {/* View Project Link */}
                <Link
                  to={`/cinematic-showcase-portfolio/${project.id}`}
                  className="flex items-center justify-center lg:space-x-2 px-6 py-3 text-xs font-semibold uppercase text-white transition-command sm:text-sm bg-red-700 rounded-lg shadow-emergency hover:bg-accent/90"
                >
                  <span>View Project</span>
                  <Icon name="ArrowRight" size={16} strokeWidth={2} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View All Projects CTA */}
        <div className="text-center mt-4 sm:mt-8">
          <Link
            to="/cinematic-showcase-portfolio"
            className="inline-flex items-center space-x-2 bg-red-700 hover:bg-accent/90 text-white px-8 py-4 rounded-lg font-semibold cinematic-transition hover:shadow-elevation uppercase"
          >
            <Icon name="Film" size={20} strokeWidth={2} />
            <span>View Complete Portfolio</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RecentWork;
